import React, { useState, useEffect } from 'react';
import { X, Search, BookOpen, Calendar, Hash, User, FileText, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useLoading } from '../contexts/LoadingContext';
import LoadingButton from './LoadingButton';
import { ScannedBook } from './ScannedBooksList';

interface EditScannedBookModalProps {
  book: ScannedBook | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (book: ScannedBook) => void;
}

const EditScannedBookModal: React.FC<EditScannedBookModalProps> = ({
  book,
  isOpen,
  onClose,
  onSave
}) => {
  const { showError, showSuccess } = useToast();
  const { isLoading, withLoading } = useLoading();
  const [editedBook, setEditedBook] = useState<ScannedBook | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Initialize edited book when modal opens
  useEffect(() => {
    if (book && isOpen) {
      setEditedBook({ ...book });
    }
  }, [book, isOpen]);

  // Handle input changes
  const handleInputChange = (field: keyof ScannedBook, value: string) => {
    if (!editedBook) return;
    
    setEditedBook({
      ...editedBook,
      [field]: value
    });
  };

  // Handle Google data changes
  const handleGoogleDataChange = (field: string, value: string) => {
    if (!editedBook) return;
    
    setEditedBook({
      ...editedBook,
      googleData: {
        ...editedBook.googleData,
        [field]: value
      } as any
    });
  };

  // Search again with Google Books
  const searchAgain = withLoading('search-google-books', async () => {
    if (!editedBook) return;

    setIsSearching(true);
    try {
      const response = await fetch('/api/books/enrich-from-google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: editedBook.title,
          author: editedBook.author
        })
      });

      if (!response.ok) {
        throw new Error('Recherche échouée');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setEditedBook({
          ...editedBook,
          enriched: true,
          enrichmentStatus: 'success',
          googleData: data.data
        });
        showSuccess('Succès', 'Données enrichies avec succès');
      } else {
        showError('Erreur', 'Aucun livre trouvé avec ces informations');
      }
    } catch (error: any) {
      showError('Erreur', error.message || 'Erreur lors de la recherche');
    } finally {
      setIsSearching(false);
    }
  });

  // Save changes
  const handleSave = () => {
    if (!editedBook) return;
    
    if (!editedBook.title.trim() || !editedBook.author.trim()) {
      showError('Erreur', 'Le titre et l\'auteur sont obligatoires');
      return;
    }

    onSave(editedBook);
    onClose();
  };

  // Format publication date for display
  const formatPublicationDate = (dateString: string) => {
    if (!dateString || dateString === 'N/A') return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0]; // Format for date input
    } catch (error) {
      return '';
    }
  };

  if (!isOpen || !editedBook) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-forest-700" />
            Modifier le livre
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <User className="h-4 w-4 mr-2 text-forest-700" />
              Informations de base
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du livre *
                </label>
                <input
                  type="text"
                  value={editedBook.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  placeholder="Titre du livre"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auteur *
                </label>
                <input
                  type="text"
                  value={editedBook.author}
                  onChange={(e) => handleInputChange('author', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  placeholder="Nom de l'auteur"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <LoadingButton
                onClick={searchAgain}
                loading={isLoading('search-google-books')}
                loadingText="Recherche..."
                className="px-4 py-2"
                variant="secondary"
                size="sm"
              >
                <Search className="h-4 w-4 mr-2" />
                Rechercher à nouveau
              </LoadingButton>
            </div>
          </div>

          {/* Google Books Data */}
          {editedBook.googleData && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-green-600" />
                Données enrichies (Google Books)
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ISBN
                  </label>
                  <input
                    type="text"
                    value={editedBook.googleData.isbn || ''}
                    onChange={(e) => handleGoogleDataChange('isbn', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                    placeholder="ISBN"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de publication
                  </label>
                  <input
                    type="date"
                    value={formatPublicationDate(editedBook.googleData.published_date || '')}
                    onChange={(e) => handleGoogleDataChange('published_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editedBook.googleData.description || ''}
                  onChange={(e) => handleGoogleDataChange('description', e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  placeholder="Description du livre"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de l'image de couverture
                </label>
                <div className="flex space-x-4">
                  <input
                    type="url"
                    value={editedBook.googleData.cover_image || ''}
                    onChange={(e) => handleGoogleDataChange('cover_image', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                    placeholder="https://example.com/cover.jpg"
                  />
                  {editedBook.googleData.cover_image && (
                    <div className="w-16 h-20 flex-shrink-0">
                      <img
                        src={editedBook.googleData.cover_image}
                        alt="Couverture"
                        className="w-full h-full object-cover rounded-lg border border-gray-200"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Manual Data Entry (if not enriched) */}
          {!editedBook.enriched && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-orange-600" />
                Informations manuelles
              </h3>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <FileText className="h-4 w-4 text-orange-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-orange-800">
                      Livre non trouvé dans Google Books
                    </h4>
                    <p className="text-sm text-orange-700 mt-1">
                      Vous pouvez ajouter manuellement les informations manquantes.
                      Le livre sera ajouté avec les données que vous fournissez.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ISBN (optionnel)
                  </label>
                  <input
                    type="text"
                    value={editedBook.googleData?.isbn || ''}
                    onChange={(e) => {
                      if (!editedBook.googleData) {
                        setEditedBook({
                          ...editedBook,
                          googleData: {
                            google_id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            isbn: e.target.value,
                            published_date: '',
                            description: '',
                            cover_image: ''
                          }
                        });
                      } else {
                        handleGoogleDataChange('isbn', e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                    placeholder="ISBN"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de publication (optionnel)
                  </label>
                  <input
                    type="date"
                    value={formatPublicationDate(editedBook.googleData?.published_date || '')}
                    onChange={(e) => {
                      if (!editedBook.googleData) {
                        setEditedBook({
                          ...editedBook,
                          googleData: {
                            google_id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            isbn: '',
                            published_date: e.target.value,
                            description: '',
                            cover_image: ''
                          }
                        });
                      } else {
                        handleGoogleDataChange('published_date', e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnel)
                </label>
                <textarea
                  value={editedBook.googleData?.description || ''}
                  onChange={(e) => {
                    if (!editedBook.googleData) {
                      setEditedBook({
                        ...editedBook,
                        googleData: {
                          google_id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                          isbn: '',
                          published_date: '',
                          description: e.target.value,
                          cover_image: ''
                        }
                      });
                    } else {
                      handleGoogleDataChange('description', e.target.value);
                    }
                  }}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  placeholder="Description du livre"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <LoadingButton
            onClick={handleSave}
            loading={isLoading('save-book')}
            loadingText="Sauvegarde..."
            className="px-4 py-2"
            variant="primary"
            size="sm"
          >
            Sauvegarder
          </LoadingButton>
        </div>
      </div>
    </div>
  );
};

export default EditScannedBookModal;
